/**
 * @file Team invitations: the validators and DTOs shared by the API and the SPA.
 *
 * An owner invites a person by email to join their workspace as a `member` (and only ever a
 * `member`). Design and what is not built: docs/TEAM.md. Pure: no I/O, no framework.
 */
import { z } from "zod";
import { INVITE_EMAIL_MAX_LENGTH, type WorkspaceRole } from "./constants.js";

/**
 * The invitation target. Trimmed and lowercased BEFORE it is stored or compared, so the one
 * canonical form is what the unique index, the member check and the accept check all see.
 */
export const invitationCreateSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "email is required")
    .max(INVITE_EMAIL_MAX_LENGTH, "email is too long")
    .email("enter a valid email address")
    .transform((value) => value.toLowerCase()),
});
export type InvitationCreateInput = z.input<typeof invitationCreateSchema>;
export type InvitationCreateBody = z.output<typeof invitationCreateSchema>;

/** A UUID-shaped string. The route re-checks it before it reaches SQL. */
const uuidLike = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

/** Token: 32 random bytes as 64 lowercase hex characters (see the API's invitation token helper). */
export const INVITE_TOKEN_HEX_LENGTH = 64;

export const invitationAcceptSchema = z.object({
  workspaceId: uuidLike,
  token: z.string().regex(/^[0-9a-f]{64}$/),
});
export type InvitationAcceptBody = z.output<typeof invitationAcceptSchema>;

/**
 * The ONE sentence every accept failure uses (unknown, expired, revoked, already used, wrong
 * account): the response must not reveal which, or it becomes a probe of invitations and addresses.
 */
export const INVITATION_NOT_VALID_MESSAGE =
  "This invitation is not valid. Ask the owner to send a new one.";

/** One pending invitation, as the owner sees it. Never carries the token or its hash. */
export interface InvitationDto {
  readonly id: string;
  readonly email: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface InvitationListDto {
  readonly invitations: readonly InvitationDto[];
  readonly truncated?: true;
}

/** POST /workspaces/:id/invitations response. `joinUrl` is present ONLY when no email could be sent. */
export interface InvitationCreatedDto {
  readonly invitation: { readonly id: string; readonly email: string; readonly expiresAt: string };
  readonly emailSent: boolean;
  readonly joinUrl?: string;
}

export interface InvitationAcceptedDto {
  readonly workspaceId: string;
  readonly workspaceName: string;
  /** The role the person now holds. `member` in the normal flow; an existing membership is never overwritten. */
  readonly role: WorkspaceRole;
}
